import { useQuery } from "@tanstack/react-query";
import { authorizedFetch } from "../auth/authorizedFetch";
import { queryClient } from "../queryClient";
import { throwIfNotOk } from "./errorEnvelope";
import type { SeedResult } from "./fga-types";
import type {
  CatalogBlueprint,
  CatalogBlueprintPlatform,
  CatalogCompany,
  CatalogResourceType,
} from "./types";

const CATALOG_BASE =
  import.meta.env.VITE_MANAGEMENT_API_URL ?? "/management-api";
const FGA_API_BASE = import.meta.env.VITE_FGA_API_URL || "/fga-api/api";

export async function catalogFetch(
  path: string,
  init?: RequestInit,
): Promise<Response> {
  const res = await authorizedFetch(`${CATALOG_BASE}${path}`, init);
  await throwIfNotOk(res);
  return res;
}

// ── Queries ───────────────────────────────────────────────────────────────────

// Every blueprint the caller may view, as defined in the Intility company.
export function useBlueprints(enabled = true) {
  return useQuery({
    queryKey: ["catalog", "blueprints"],
    enabled,
    queryFn: async () => {
      const res = await catalogFetch("/catalog/platforms/blueprints");
      return res.json() as Promise<CatalogBlueprint>;
    },
  });
}

// The blueprint of one Platform, or null when the Platform has no blueprint.
export function usePlatformBlueprint(slug: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["catalog", "blueprint", slug],
    enabled: enabled && !!slug,
    queryFn: async () => {
      const res = await authorizedFetch(
        `${CATALOG_BASE}/catalog/platforms/${encodeURIComponent(slug ?? "")}/blueprint`,
      );
      if (res.status === 404) return null;
      await throwIfNotOk(res);
      return res.json() as Promise<CatalogBlueprintPlatform>;
    },
  });
}

export function useResourceTypes() {
  return useQuery({
    queryKey: ["catalog", "resource-types"],
    queryFn: async () => {
      const res = await catalogFetch("/catalog/resource-types");
      const data = (await res.json()) as { items: CatalogResourceType[] };
      return data.items;
    },
  });
}

// The companies the viewer can see (member or reader on the company), with their display
// names. fga-api serves the names from the catalog table, which onboarding writes.
export function useCompanies() {
  return useQuery({
    queryKey: ["catalog", "companies"],
    queryFn: async () => {
      const res = await authorizedFetch(`${FGA_API_BASE}/companies`);
      await throwIfNotOk(res, "fga");
      return (await res.json()) as CatalogCompany[];
    },
  });
}

// Which companies a Platform is already available for in the access store — the
// company-link tuples the seed operation wrote — and every onboarded company (each company
// under provider:intility, ADR-0058 decision 2). fga-management-api answers this by reading
// fga-api with its service identity, gated on blueprint maintainer (ADR-0043: gate first,
// then the service acts), because a blueprint maintainer does not necessarily hold
// access-store admin on the platform objects.
export function useSeededCompanies(platformSlug: string) {
  return useQuery({
    queryKey: ["catalog", "seeded-companies", platformSlug],
    enabled: !!platformSlug,
    queryFn: async () => {
      const res = await catalogFetch(
        `/access-store/platforms/${encodeURIComponent(platformSlug)}/companies`,
      );
      return (await res.json()) as { seeded: string[]; companies: string[] };
    },
  });
}

// ── Imperative mutations (called at submit time) ───────────────────────────────

export async function createResourceType(req: {
  slug: string;
  displayName: string;
  description?: string;
  nameRegex?: string;
  isStatic: boolean;
}): Promise<CatalogResourceType> {
  const res = await catalogFetch("/catalog/resource-types", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  });
  const resourceType = await res.json();
  queryClient.invalidateQueries({ queryKey: ["catalog"] });
  return resourceType;
}

export async function createUsage(
  platformSlug: string,
  req: { typeId: string; parentUsageId?: string | null; qualifiedName: string },
): Promise<{ id: string }> {
  const res = await catalogFetch(
    `/catalog/platforms/${encodeURIComponent(platformSlug)}/usages`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    },
  );
  const usage = await res.json();
  queryClient.invalidateQueries({ queryKey: ["catalog"] });
  return usage;
}

export async function deleteUsage(usageId: string): Promise<void> {
  await catalogFetch(`/catalog/usages/${usageId}`, { method: "DELETE" });
  queryClient.invalidateQueries({ queryKey: ["catalog"] });
}

// Makes a Platform available to one company or all of them (idempotent). Goes through
// fga-management-api, which seeds the access store with its service identity after gating
// the caller on blueprint maintainer.
export async function makePlatformAvailable(
  platformSlug: string,
  req: { companySlug?: string; all?: boolean },
): Promise<SeedResult> {
  const res = await catalogFetch(
    `/access-store/platforms/${encodeURIComponent(platformSlug)}/companies`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    },
  );
  const result = (await res.json()) as SeedResult;
  queryClient.invalidateQueries({
    queryKey: ["catalog", "seeded-companies"],
  });
  return result;
}
